import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "oidc-client-ts";
import { getUserManager } from "./userManager";
import { getOidcSettings } from "./oidcConfig";
import { asError, eventBus } from "@optimyl/events";
import { getConfig } from "../config";

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (forcePrompt?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const config = getConfig();
  const authEnabled = config.AUTH_ENABLED;

  // When auth is disabled, skip loading entirely — no Identity Server needed.
  const [isLoading, setIsLoading] = useState(authEnabled);

  // Only create the UserManager when auth is enabled — avoids calling the Identity Server
  // discovery endpoint when it's not running (local dev with AUTH_ENABLED: false).
  const userManager = useMemo(() => (authEnabled ? getUserManager() : null), [authEnabled]);

  useEffect(() => {
    if (!userManager) return;

    userManager.getUser()
      .then((existingUser) => {
        if (existingUser && !existingUser.expired) {
          setUser(existingUser);
        }
      })
      .catch((err) => {
        eventBus.publish("error", {
          error: asError(err),
          context: "AuthProvider.getUser",
        });
      })
      .finally(() => setIsLoading(false));

    // Listen for token events
    const onUserLoaded = (loadedUser: User) => setUser(loadedUser);
    const onUserUnloaded = () => setUser(null);
    const onSilentRenewError = (error: Error) => {
      eventBus.publish("error", { error, context: "oidc.silentRenew" });
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [userManager]);

  const login = useCallback((forcePrompt = false) => {
    if (!userManager) return Promise.resolve();
    const sfEnv = sessionStorage.getItem("sfEnv");
    const extraQueryParams: Record<string, string> = {};
    if (sfEnv) extraQueryParams["sf_env"] = sfEnv;
    return userManager.signinRedirect({
      extraQueryParams,
      ...(forcePrompt ? { prompt: "login" } : {}),
    });
  }, [userManager]);

  // Logout uses direct window.location.href instead of oidc-client-ts signoutRedirect().
  // Why: signoutRedirect() is unreliable on Safari — ITP blocks cross-origin cookies/storage
  // that the library depends on. Direct redirect works in all browsers.
  // Why redirect BEFORE clearing state: setUser(null) triggers React re-render → AuthGuard
  // sees no user → calls login() via React Router, which cancels the logout navigation.
  // Local OIDC state is cleaned up on the /logged-out page after the redirect completes.
  const logout = useCallback(async () => {
    const cfg = getConfig();
    try {
      const currentUser = userManager ? await userManager.getUser() : null;
      const idToken = currentUser?.id_token;

      const { client_id } = getOidcSettings();
      const params = new URLSearchParams({
        client_id,
        post_logout_redirect_uri: `${window.location.origin}/logged-out`,
      });
      if (idToken) params.set("id_token_hint", idToken);

      window.location.href = `${cfg.IDENTITY_URL}/connect/logout?${params}`;
    } catch (err) {
      eventBus.publish("error", {
        error: asError(err),
        context: "AuthProvider.logout",
      });
      window.location.href = `${window.location.origin}/logged-out`;
    }
  }, [userManager]);

  const getAccessToken = useCallback(
    () => user?.access_token ?? null,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user && !user.expired,
      isLoading,
      login,
      logout,
      getAccessToken,
    }),
    [user, isLoading, login, logout, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
